import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { Lock, Mail, Shield } from 'lucide-react';
import { login } from '@/lib/api/admin';
import { API_BASE_URL, describeError } from '@/lib/api/client';
import { hasSession } from '@/lib/api/tokens';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '@/components/shell/theme-toggle';

/** Only a same-app path may be a return target — never something pasted into the link. */
function safeFrom(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : '/';
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const submit = useMutation({
    mutationFn: () => login(email.trim(), password),
    onSuccess: () => navigate(safeFrom(location.state), { replace: true }),
  });

  if (hasSession() && !submit.isPending && !submit.isError) return <Navigate to={safeFrom(location.state)} replace />;

  const onSubmit = (e: FormEvent) => { e.preventDefault(); submit.mutate(); };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Backdrop: a soft brand glow, nothing that competes with the form. */}
      <div aria-hidden className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-accent/10 blur-3xl" />
      <div className="absolute right-4 top-4"><ThemeToggle compact /></div>

      <form onSubmit={onSubmit} className="relative w-full max-w-sm rounded-2xl border border-line bg-surface p-7 shadow-card animate-pop">
        <div className="mb-7 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-white shadow-sm"><Shield size={22} /></span>
          <div>
            <h1 className="text-base font-bold text-ink">Dravizo Admin</h1>
            <p className="text-xs text-muted">Вход для администраторов платформы</p>
          </div>
        </div>

        <div className="space-y-4">
          <Input label="E-mail" type="email" autoComplete="username" required autoFocus leading={<Mail size={15} />} value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Пароль" type="password" autoComplete="current-password" required minLength={8} leading={<Lock size={15} />} value={password} onChange={(e) => setPassword(e.target.value)} />

          {submit.isError && (
            <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-xs leading-5 text-danger">{describeError(submit.error)}</p>
          )}

          <Button type="submit" className="w-full" loading={submit.isPending}>Войти</Button>
        </div>

        <p className="mt-6 truncate text-center font-mono text-[11px] text-faint" title={API_BASE_URL}>{API_BASE_URL}</p>
      </form>
    </main>
  );
}
